import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  styled,
  Typography,
  Link,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import CallIcon from "@mui/icons-material/Call";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import { HashLink } from "react-router-hash-link";
import { blue } from "@mui/material/colors";
import NavigationIcon from '@mui/icons-material/Navigation';
import "./Footer.css";

/* ---------- HELPERS ---------- */

const Root = styled("div")(({ theme }) => ({
  width: "100%",
  ...theme.typography.body2,
  "& > :not(style) + :not(style)": {
    marginTop: theme.spacing(2),
  },
}));

/* ---------- CUSTOM LINK ---------- */

const FooterLink = ({ children, ...props }) => (
  <Link
    underline="none"
    color="inherit"
    {...props}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      fontWeight: 500,
      textDecoration: "none",
      transition: "all 0.25s ease",
      "&:hover": {
        color: "primary.main",
        transform: "translateX(4px)",
      },
      "&:visited": {
        color: "inherit",
      },
      "&:focus-visible": {
        outline: "2px dashed #0f7ca4",
        outlineOffset: "4px",
      },
    }}
  >
    {children}
  </Link>
);

/* ---------- FOOTER ---------- */

const Footer = () => {
  return (
    <footer>
      <Box
        sx={{
          bgcolor: "secondary.main",
          color: "text.primary",
          pb: 2,
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {/* ---------- BRAND / CONTACT ---------- */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  fontWeight: 700,
                }}
              >
                <Avatar className="footer-mascot" sx={{ bgcolor: "white" }}>
                  <img
                    src="/logo2.png"
                    alt="Den Den Claw"
                    style={{ height: 36 }}
                  />
                </Avatar>
                Den Den Claw
              </Typography>

              <Divider className="footer-divider" sx={{ my: 2 }} />

              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ bgcolor: blue[300] }}>
                    <LocationOnIcon />
                  </Avatar>
                  <FooterLink
                    href="https://maps.app.goo.gl/divxnw3iNqiqT3ru8"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    691 Somerset St W, Ottawa, ON
                  </FooterLink>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ bgcolor: blue[300] }}>
                    <EmailIcon />
                  </Avatar>
                  <FooterLink href="mailto:dendendori@gmail.com">
                    dendendori@gmail.com
                  </FooterLink>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ bgcolor: blue[300] }}>
                    <CallIcon />
                  </Avatar>
                  <FooterLink href="tel:16134002909">
                    (613) 400-2909
                  </FooterLink>
                </Stack>
              </Stack>
            </Grid>

            {/* ---------- QUICK LINKS ---------- */}
            <Grid item xs={12} sm={6} md={4}>
              <Root>
                <Divider>
                  <Chip label="Explore" />
                </Divider>
              </Root>

              <Stack spacing={4.5} sx={{ mt: 2 }}>
                <FooterLink component={HashLink} to="/services">
                  All Services →
                </FooterLink>

                <FooterLink component={HashLink} to="/explore/events">
                  Event Info →
                </FooterLink>

                <FooterLink component={HashLink} to="/explore/artist-alley">
                  Apply to be in the Artist Alley →
                </FooterLink>

                <FooterLink component={HashLink} to="/explore/pop-ups">
                  Pop-Up Schedule &amp; Vendor Booking →
                </FooterLink>

                <FooterLink component={HashLink} to="/secret-code">
                  Secret Code →
                </FooterLink>
              </Stack>
            </Grid>

            {/* ---------- SOCIAL ---------- */}
            <Grid item xs={12} sm={12} md={4}>
              <Root>
                <Divider>
                  <Chip label="Find Us" />
                </Divider>
              </Root>

              <Stack spacing={1.5} sx={{ mt: 2 }}>
                 <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar className="social-avatar sparkle-mode">
                    <NavigationIcon />
                  </Avatar>
                  <FooterLink href="/contact">
                    Visit Us
                  </FooterLink>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar className="social-avatar sparkle-mode">
                    <InstagramIcon />
                  </Avatar>
                  <FooterLink
                    href="https://instagram.com/dendenclaw"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Instagram
                  </FooterLink>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar className="social-avatar sparkle-mode">
                    <FacebookIcon />
                  </Avatar>
                  <FooterLink
                    href="https://facebook.com/profile.php?id=61554656757911"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Facebook
                  </FooterLink>
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="body2"
            sx={{ textAlign: "center", opacity: 0.7 }}
          >
            © {new Date().getFullYear()} Den Den Claw · All rights reserved
          </Typography>
        </Container>
      </Box>
    </footer>
  );
};

export default Footer;
